import { useId } from 'react';

import type { FeedbackStatus } from '@/entities/feedback';

import styles from './StatusIcon.module.css';

interface Props {
  status: FeedbackStatus;
  /**
   * Plays the status' own motion. Only the CURRENT status of an open suggestion is animated;
   * lists and historical timeline entries stay still. `prefers-reduced-motion` turns every
   * animation off in CSS, so the static icon + color + label always tell the status.
   */
  animated?: boolean;
  size?: number;
}

/**
 * One distinct icon per status, with a small motion of its own (inside the icon, never the
 * label, never a layout change):
 *  received       - a dot settles into the tray (once)
 *  under_review   - an arc slowly circles inside the magnifier lens
 *  clarification  - the question mark gives a gentle pulse every few seconds
 *  planned        - a glint sweeps across the star every few seconds
 *  in_progress    - the gear turns slowly
 *  implemented    - the check mark draws itself (once)
 *  not_planned    - the circle settles softly (once)
 */
export function StatusIcon({ status, animated = false, size = 20 }: Props) {
  const clip = useId();
  return (
    <svg
      className={styles.icon}
      data-status={status}
      data-animated={animated ? 'true' : 'false'}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {status === 'received' && (
        <>
          <path d="M3.5 14.5 6.2 6.5A1.5 1.5 0 0 1 7.6 5.5h8.8a1.5 1.5 0 0 1 1.4 1L20.5 14.5" />
          <path d="M3.5 14.5h4.6a1 1 0 0 1 1 .8 3 3 0 0 0 5.8 0 1 1 0 0 1 1-.8h4.6V18a1.5 1.5 0 0 1-1.5 1.5h-14A1.5 1.5 0 0 1 3.5 18z" />
          <circle
            className={styles.receivedDot}
            cx="12"
            cy="9.5"
            r="1.6"
            fill="currentColor"
            stroke="none"
          />
        </>
      )}
      {status === 'under_review' && (
        <>
          <circle cx="10.5" cy="10.5" r="6.5" />
          <path d="m15.5 15.5 5 5" />
          <g className={styles.reviewArc}>
            <path d="M10.5 6.8a3.7 3.7 0 0 1 3.7 3.7" strokeWidth="1.6" />
          </g>
        </>
      )}
      {status === 'clarification_needed' && (
        <>
          <path d="M5 4.5h14A1.5 1.5 0 0 1 20.5 6v9a1.5 1.5 0 0 1-1.5 1.5h-7.2L8 20.2v-3.7H5A1.5 1.5 0 0 1 3.5 15V6A1.5 1.5 0 0 1 5 4.5z" />
          <g className={styles.question}>
            <path d="M10.2 8.8a1.9 1.9 0 1 1 2.6 1.8c-.6.3-.8.7-.8 1.3" />
            <circle cx="12" cy="13.9" r="0.4" fill="currentColor" />
          </g>
        </>
      )}
      {status === 'planned' && (
        <>
          <defs>
            <clipPath id={clip}>
              <path d="m12 3.2 2.6 5.4 5.9.8-4.3 4.1 1 5.9L12 16.6l-5.2 2.8 1-5.9-4.3-4.1 5.9-.8z" />
            </clipPath>
          </defs>
          <path d="m12 3.2 2.6 5.4 5.9.8-4.3 4.1 1 5.9L12 16.6l-5.2 2.8 1-5.9-4.3-4.1 5.9-.8z" />
          <g clipPath={`url(#${clip})`}>
            <g transform="rotate(20 12 12)">
              <rect
                className={styles.glint}
                x="-6"
                y="-2"
                width="3.5"
                height="28"
                fill="currentColor"
                stroke="none"
              />
            </g>
          </g>
        </>
      )}
      {status === 'in_progress' && (
        <g className={styles.gear}>
          <path d="M10.2 5.7 L10.4 3.1 L13.6 3.1 L13.8 5.7 L15.2 6.2 L17.2 4.6 L19.4 6.8 L17.8 8.8 L18.3 10.2 L20.9 10.4 L20.9 13.6 L18.3 13.8 L17.8 15.2 L19.4 17.2 L17.2 19.4 L15.2 17.8 L13.8 18.3 L13.6 20.9 L10.4 20.9 L10.2 18.3 L8.8 17.8 L6.8 19.4 L4.6 17.2 L6.2 15.2 L5.7 13.8 L3.1 13.6 L3.1 10.4 L5.7 10.2 L6.2 8.8 L4.6 6.8 L6.8 4.6 L8.8 6.2Z" />
          <circle cx="12" cy="12" r="2.6" />
        </g>
      )}
      {status === 'implemented' && (
        <>
          <circle className={styles.settle} cx="12" cy="12" r="8.8" />
          <path className={styles.check} d="m7.8 12.4 3 3 5.6-6.4" pathLength="1" />
        </>
      )}
      {status === 'not_planned' && (
        <>
          <circle className={styles.settle} cx="12" cy="12" r="8.8" />
          <path d="M8 12h8" />
        </>
      )}
    </svg>
  );
}
